import React, { useEffect, useState, useCallback } from 'react';

// Named, reusable subject/body pairs — shared across Campaigns,
// Interconnecting, and Direct Email composers (backend/models/
// emailTemplateModel.js). Distinct from each feature's own per-community
// "draft" (an in-progress working copy) — a saved template here is meant
// to be loaded into a fresh compose for ANY community/campaign, not tied
// to one. Some templates use placeholders (e.g. {{suggestedPeopleBlock}})
// that only resolve in specific composers — loading one into the wrong
// context just leaves that token as literal visible text, which is a
// content mistake for the admin to notice, not something this component
// tries to prevent.
// basePath defaults to the convention used by GranularInterconnectingEmails.js
// and DirectEmailTab.js's own authedFetch (which does NOT auto-prepend
// "/admin" — callers include it themselves). EmailManagerTab.js's
// authedFetch is the odd one out (it DOES auto-prepend "/admin"), so it
// passes basePath="/email-templates" to avoid a double "/admin/admin/..."
// that silently falls through to the SPA's catch-all route instead of the
// real API, returning HTML where JSON was expected.
const TemplateLibrary = ({ authedFetch, subject, body, onLoad, basePath = '/admin/email-templates' }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchTemplates = useCallback(async () => {
    try {
      const data = await authedFetch(basePath);
      setTemplates(data);
    } catch (err) {
      setError(err.message);
    }
  }, [authedFetch, basePath]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleLoad = (id) => {
    setSelectedId(id);
    if (!id) return;
    const template = templates.find((t) => t._id === id);
    if (template) onLoad({ subject: template.subject, body: template.body });
  };

  const handleSaveAs = async () => {
    const name = window.prompt('Name this template:');
    if (!name || !name.trim()) return;
    setSaving(true);
    setError('');
    try {
      const created = await authedFetch(basePath, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, subject, body }),
      });
      setTemplates((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedId(created._id);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    const template = templates.find((t) => t._id === selectedId);
    if (!window.confirm(`Delete the saved template "${template?.name || ''}"? This can't be undone.`)) return;
    try {
      await authedFetch(`${basePath}/${selectedId}`, { method: 'DELETE' });
      setTemplates((prev) => prev.filter((t) => t._id !== selectedId));
      setSelectedId('');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="as-email-field as-template-library">
      <label>Saved templates</label>
      {error && <div className="as-error">{error}</div>}
      <div className="as-template-library-row">
        <select value={selectedId} onChange={(e) => handleLoad(e.target.value)}>
          <option value="">Load a saved template…</option>
          {templates.map((t) => (
            <option key={t._id} value={t._id}>{t.name}</option>
          ))}
        </select>
        <button type="button" className="as-filter-tab" onClick={handleSaveAs} disabled={saving || (!subject && !body)}>
          {saving ? 'Saving…' : '💾 Save as…'}
        </button>
        {selectedId && (
          <button type="button" className="as-filter-tab" onClick={handleDelete}>
            🗑 Delete
          </button>
        )}
      </div>
    </div>
  );
};

export default TemplateLibrary;
